import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Link, useNavigate } from "react-router-dom";
import AuthLayout from "./AuthLayout";
import { AuthField, AuthPasswordField, AuthSubmit, authLinkClass } from "./AuthField";
import { useScriptMode } from "../../contexts/ScriptModeContext";
import { useAuth } from "../../contexts/AuthContext";
import { useLoginMutation, useLazyGetMeQuery } from "../../api/authApi";
import { useToast } from "../../components/ui/Toast";
import { homePathForRole } from "../../lib/adminFormat";

export default function LoginPage() {
  const { t } = useScriptMode();
  const { setSession } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();

  const schema = yup.object({
    email: yup.string().email(t("validation_email")).required(t("validation_required")),
    password: yup.string().required(t("validation_required")),
  });

  const { register, handleSubmit, formState: { errors } } = useForm({
    resolver: yupResolver(schema),
    defaultValues: { email: "", password: "" },
  });

  const [login, { isLoading }] = useLoginMutation();
  const [triggerGetMe] = useLazyGetMeQuery();

  const onSubmit = async (values) => {
    const result = await login(values);
    if (result.error) {
      if (result.error.status === 401) {
        toast.error(t("login_error_invalid"));
      } else if (result.error.status === 429) {
        // backend message contains the human-readable lock duration
        toast.error(result.error.message || t("login_error_locked", { minutes: 30 }));
      } else if (result.error.type === "MAINTENANCE") {
        toast.error(t("maintenance_message"));
      } else {
        toast.error(result.error.message || t("login_error_generic"));
      }
      return;
    }
    const { token, data } = result.data;
    setSession({ token, user: data?.user });
    // Refresh /me through the cache so AppShell sees the user without an extra fetch
    triggerGetMe();
    navigate(homePathForRole(data?.user?.role), { replace: true });
  };

  return (
    <AuthLayout
      title={t("login_title")}
      subtitle={t("login_subtitle")}
      footer={
        <p className="text-center">
          {t("login_no_account")}{" "}
          <Link to="/kayit" className={authLinkClass}>
            {t("login_signup_link")}
          </Link>
        </p>
      }
    >
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5">
        <AuthField
          id="email"
          label={t("email")}
          type="email"
          autoComplete="email"
          placeholder="ornek@mail.com"
          disabled={isLoading}
          error={errors.email?.message}
          {...register("email")}
        />

        <AuthPasswordField
          id="password"
          label={t("password")}
          autoComplete="current-password"
          placeholder="••••••••"
          disabled={isLoading}
          error={errors.password?.message}
          showLabel={t("password_show")}
          hideLabel={t("password_hide")}
          {...register("password")}
        />

        <div className="flex justify-end">
          <Link to="/sifremi-unuttum" className={`text-base ${authLinkClass}`}>
            {t("login_forgot_password")}
          </Link>
        </div>

        <AuthSubmit loading={isLoading}>{t("login_submit")}</AuthSubmit>
      </form>
    </AuthLayout>
  );
}
